import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiUsers, FiDollarSign, FiTool, FiShield, FiSettings, FiTrendingUp, FiBarChart3 } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiHome,
      title: 'Comprehensive Management',
      description: 'Full-service property management including tenant placement, rent collection, maintenance coordination, and detailed financial reporting.',
      features: ['Tenant Screening', 'Rent Collection', 'Property Inspections', 'Financial Reports']
    },
    {
      icon: FiUsers,
      title: 'Tenant Care Excellence',
      description: 'Professional tenant acquisition and retention strategies ensuring stable occupancy and satisfied residents.',
      features: ['24/7 Support', 'Quick Response', 'Tenant Retention', 'Quality Screening']
    },
    {
      icon: FiDollarSign,
      title: 'Financial Efficiency',
      description: 'Timely rent collection, accurate invoicing, budget preparation, and transparent financial reporting for maximum profitability.',
      features: ['Rent Optimization', 'Budget Planning', 'Cost Control', 'ROI Analysis']
    },
    {
      icon: FiTool,
      title: 'Maintenance Excellence',
      description: 'Proactive property maintenance and repairs to preserve value and ensure tenant satisfaction with quality workmanship.',
      features: ['Preventive Care', 'Emergency Repairs', 'Quality Control', 'Value Preservation']
    },
    {
      icon: FiShield,
      title: 'Legal & Compliance',
      description: 'Expert lease administration and regulatory compliance to protect your investment and minimize legal risks.',
      features: ['Legal Protection', 'Compliance Monitoring', 'Risk Mitigation', 'Documentation']
    },
    {
      icon: FiSettings,
      title: 'Vendor Coordination',
      description: 'Efficient management of service providers ensuring quality work at competitive prices for all property needs.',
      features: ['Contractor Network', 'Quality Assurance', 'Cost Management', 'Project Oversight']
    },
    {
      icon: FiTrendingUp,
      title: 'Value Optimization',
      description: 'Strategic property enhancements and improvements to maximize performance and increase investment returns.',
      features: ['Market Analysis', 'Improvement Planning', 'Value Enhancement', 'Performance Tracking']
    },
    {
      icon: FiBarChart3,
      title: 'Real Estate Investment',
      description: 'Bespoke investment strategies, market analysis, property procurement, and portfolio optimization services.',
      features: ['Investment Strategy', 'Market Research', 'Portfolio Growth', 'Financial Support']
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Comprehensive Property Management Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From tenant placement to investment optimization, we provide complete property management solutions tailored to Calgary and Airdrie markets.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100"
            >
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-xl mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
              
              <ul className="space-y-2">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="text-sm text-gray-700 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
          >
            Get Your Free Property Analysis
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;